import { useMutation, useQuery } from "@tanstack/react-query"
import { useDispatch, useSelector } from "react-redux"
import { useEffect } from "react"
import { deleteOrganization, listOrganizations, switchOrganization } from "@/lib/http/organization"
import { setOrganizations, setActiveOrganization, updateActiveOrganization } from "@/lib/store/organization"
import type { AppDispatch, RootState } from "@/lib/store"
import { Organization } from "@/types/api"
import { useNavigate, useSearchParams } from "react-router-dom"
import { getRedirectUrl } from "@/lib/utils/route"
import { ROUTES } from "@/routes/path"
import { useAuth } from "./useAuth"

export const useSwitchOrganization = () =>
  useMutation({
    mutationFn: (organizationId: string) => switchOrganization(organizationId),
  })

export const useOnOrganizationEvent = () => {
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const { token: authToken } = useAuth()

  const { refetch: refetchOrganizations } = useListOrganizations(!!authToken)

  return async () => {
    const { data: organizations } = await refetchOrganizations()

    if (organizations && organizations?.length > 0) {
      dispatch(setOrganizations(organizations))
      dispatch(setActiveOrganization(organizations[0]))

      await switchOrganization(organizations[0].id).then(() => {
        window.location.href = getRedirectUrl(searchParams, ROUTES.DASHBOARD.STUDIO.INDEX)
      })
    } else {
      navigate(ROUTES.DASHBOARD.ORGANIZATION.INDEX, { replace: true })
    }
  }
}

export const useOnSetOrganization = () => {
  const dispatch = useDispatch<AppDispatch>()

  return (organizations: Organization[], activeOrganization: Organization) => {
    dispatch(setOrganizations(organizations))
    dispatch(setActiveOrganization(activeOrganization))
  }
}

export const useListOrganizations = (enabled: boolean = true) => {
  const dispatch = useDispatch<AppDispatch>()
  const { activeOrganization } = useSelector((state: RootState) => state.organization)

  const query = useQuery({
    queryKey: ["organizations"],
    queryFn: listOrganizations,
    staleTime: 30 * 1000, // 30 seconds
    enabled,
  })

  useEffect(() => {
    if (query.data && query.data.length > 0) {
      dispatch(setOrganizations(query.data))

      const updatedOrganization = query.data.find((org) => org.id === activeOrganization?.id)
      dispatch(setActiveOrganization(updatedOrganization || query.data[0]))
    }
  }, [query.data, activeOrganization, dispatch])

  return query
}

export const useActiveOrganization = () => {
  const { activeOrganization } = useSelector((state: RootState) => state.organization)
  return activeOrganization
}

export const useOrganizations = () => {
  const { organizations } = useSelector((state: RootState) => state.organization)
  return organizations
}

export const useUpdateActiveOrganization = () => {
  const dispatch = useDispatch<AppDispatch>()

  return (organization: Organization) => {
    dispatch(updateActiveOrganization(organization))
  }
}

export const useSetActiveOrganization = () => {
  const dispatch = useDispatch<AppDispatch>()

  return (organization: Organization) => {
    dispatch(setActiveOrganization(organization))
  }
}

export const useDeleteOrganization = (options: {
  onSuccess?: () => void
  onError?: (error: any) => void
  onSettled?: () => void
}) =>
  useMutation({
    mutationFn: (organizationId: string) => deleteOrganization(organizationId),
    onSuccess: options.onSuccess,
    onSettled: options.onSettled,
    onError: options.onError,
  })
